import { Divider, Stack, Typography } from "@mui/material";
import { ManifestReviewSummaryQuery } from "generated-graphql/graphql";

type AssigneeCounts = ManifestReviewSummaryQuery["staffManifestReviewSummary"]["assignedCounts"];

// Cap the scrollable list at ~6 rows tall; a partial 7th row peeks to signal there's
// more to scroll. Each row is body2 text (~20px) + py:0.5 padding (8px) ≈ 28px.
const ROW_HEIGHT_PX = 28;
const MAX_VISIBLE_ROWS = 6.5;

/**
 * Per-reviewer assignment counts, rendered inside the Summary panel. Presentational —
 * the counts come from the panel's query. Renders nothing when no one has assignments.
 */
export function AssignedByStaff({ assignedCounts }: { assignedCounts: AssigneeCounts }) {
  if (assignedCounts.length === 0) return null;

  return (
    <Stack sx={{ width: "100%" }}>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
        Assignments by reviewer
      </Typography>
      <Stack
        divider={<Divider flexItem />}
        sx={{ maxHeight: ROW_HEIGHT_PX * MAX_VISIBLE_ROWS, overflowY: "auto", pr: 0.5 }}
      >
        {assignedCounts.map(({ user, count }) => {
          const name = user.person
            ? `${user.person.first ?? ""} ${user.person.last ?? ""}`.trim()
            : "";
          return (
            <Stack key={user.id} direction="row" justifyContent="space-between" sx={{ py: 0.5 }}>
              <Typography variant="body2">{name || user.email}</Typography>
              <Typography variant="body2" fontWeight="bold">
                {count}
              </Typography>
            </Stack>
          );
        })}
      </Stack>
    </Stack>
  );
}
